import * as React from "react";

/**
 * Light/dark theming.
 *
 * shadcn's scaffolding reaches for `next-themes`, which exists to solve a
 * Next.js server-rendering problem this application does not have. Thirty lines
 * of context is the whole requirement: persist a choice, follow the system when
 * there isn't one, and toggle a class on `<html>`.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "nwga-theme";

interface ThemeContextValue {
	readonly theme: Theme;
	/** What is actually on screen once `system` has been resolved. */
	readonly resolvedTheme: ResolvedTheme;
	readonly setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function systemTheme(): ResolvedTheme {
	if (typeof window === "undefined") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function readStoredTheme(): Theme {
	if (typeof window === "undefined") return "system";
	const stored = window.localStorage.getItem(STORAGE_KEY);
	return stored === "light" || stored === "dark" || stored === "system"
		? stored
		: "system";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [theme, setThemeState] = React.useState<Theme>(readStoredTheme);
	const [systemPreference, setSystemPreference] =
		React.useState<ResolvedTheme>(systemTheme);

	// Track the OS preference so `system` stays live rather than snapshotting at
	// first render.
	React.useEffect(() => {
		const query = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = (event: MediaQueryListEvent) => {
			setSystemPreference(event.matches ? "dark" : "light");
		};
		query.addEventListener("change", onChange);
		return () => query.removeEventListener("change", onChange);
	}, []);

	const resolvedTheme = theme === "system" ? systemPreference : theme;

	React.useEffect(() => {
		const root = document.documentElement;
		root.classList.toggle("dark", resolvedTheme === "dark");
		root.style.colorScheme = resolvedTheme;
	}, [resolvedTheme]);

	const setTheme = React.useCallback((next: Theme) => {
		window.localStorage.setItem(STORAGE_KEY, next);
		setThemeState(next);
	}, []);

	const value = React.useMemo<ThemeContextValue>(
		() => ({ theme, resolvedTheme, setTheme }),
		[theme, resolvedTheme, setTheme],
	);

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

export function useTheme(): ThemeContextValue {
	const context = React.useContext(ThemeContext);
	if (context === null) {
		throw new Error("useTheme must be used inside a <ThemeProvider>");
	}
	return context;
}

/**
 * Applies the stored theme before React hydrates.
 *
 * Injected as a blocking inline script in the HTML shell; without it the page
 * paints light and then snaps to dark.
 */
export const themeInitScript = `(function(){try{var t=localStorage.getItem(${JSON.stringify(
	STORAGE_KEY,
)});var d=t==="dark"||((t===null||t==="system")&&matchMedia("(prefers-color-scheme: dark)").matches);document.documentElement.classList.toggle("dark",d);document.documentElement.style.colorScheme=d?"dark":"light";}catch(e){}})();`;
